import { Button, Modal, Typography } from "antd";
import React, { useState, useEffect } from "react";
import { useGetMyFiles } from "~/services/file";
import { TFile } from "~/types";
import { SortOrder } from "~/utils/constants";
import { DocumentList } from "./document-list";
import { DocumentTypeSelection } from "./document-type-selection";
import { DocumentUploadModal } from "./document-upload-modal";
import { useNotification } from "~/hooks";

const { Title, Text } = Typography;

interface DocumentPreviewProps {
  doc: TFile;
}

const DocumentPreview: React.FC<DocumentPreviewProps> = ({ doc }) => {
  const fileUrl = `${doc.filePath}`;
  const mimeType = doc.mimeType || getMimeTypeFromFileName(doc.fileName);
  const [canLoad, setCanLoad] = useState<boolean | null>(null);

  const canLoadFile = async (url: string) => {
    try {
      const response = await fetch(url, { method: "GET" });
      return response.ok;
    } catch (error) {
      console.error("Error checking file:", error);
      return false;
    }
  };

  useEffect(() => {
    canLoadFile(fileUrl).then((result) => setCanLoad(result));
  }, [fileUrl]);

  if (canLoad === null) {
    return <Text>Loading...</Text>;
  }

  if (!canLoad) {
    return (
      <div style={{ textAlign: "center" }}>
        <Text>
          Unable to load the document. The file may not exist or the server is
          not running.
        </Text>
      </div>
    );
  }

  switch (mimeType) {
    case "image/jpeg":
    case "image/png":
      return (
        <img
          src={fileUrl}
          alt={doc.title}
          style={{ maxWidth: "100%", maxHeight: "70vh", objectFit: "contain" }}
          onError={() => setCanLoad(false)}
        />
      );
    case "application/pdf":
      return (
        <embed
          src={fileUrl}
          type="application/pdf"
          style={{ width: "100%", height: "70vh" }}
          onError={() => setCanLoad(false)}
        />
      );
    case "application/msword":
    case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    case "text/csv":
      return (
        <div style={{ textAlign: "center" }}>
          <Text>
            This file type ({mimeType.split("/")[1].toUpperCase()}) cannot be
            previewed in the browser.
          </Text>
          <Button
            type="primary"
            href={fileUrl}
            download={doc.fileName || "document"}
            style={{ marginTop: 16 }}
          >
            Download File
          </Button>
        </div>
      );
    default:
      return <Text>Unsupported file type</Text>;
  }
};

const getMimeTypeFromFileName = (fileName?: string) => {
  if (!fileName) {
    return "application/octet-stream";
  }
  const extension = fileName.split(".").pop()?.toLowerCase();
  switch (extension) {
    case "jpg":
    case "jpeg":
      return "image/jpeg";
    case "png":
      return "image/png";
    case "pdf":
      return "application/pdf";
    case "doc":
      return "application/msword";
    case "docx":
      return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    case "csv":
      return "text/csv";
    default:
      return "application/octet-stream";
  }
};

export const Documents: React.FC = () => {
  const [selectedDoc, setSelectedDoc] = useState<TFile | null>(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [uploadVisible, setUploadVisible] = useState(false);
  const [documentType, setDocumentType] = useState<string | null>(null);
  const { error } = useNotification();

  const {
    data: filesData,
    isLoading,
    refetch,
  } = useGetMyFiles({
    pageSize: 10,
    sortByCreatedAt: SortOrder.DESC,
  });

  const documents = filesData?.items || [];

  const handleCreateDocument = (type: string) => {
    setDocumentType(type);
    setUploadVisible(true);
  };

  const viewDocument = (doc: TFile) => {
    if (!doc.filePath) {
      error({
        message: "Cannot view document",
        description: "Document URL is missing.",
      });
      return;
    }
    setSelectedDoc(doc);
    setPreviewVisible(true);
  };

  const downloadDocument = async (doc: TFile) => {
    if (!doc.filePath) {
      error({
        message: "Cannot download document",
        description: "Document URL is missing.",
      });
      return;
    }
    try {
      const response = await fetch(doc.filePath, { method: "GET" });
      if (!response.ok) throw new Error("Failed to fetch file");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = doc.fileName || "document";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      error({
        message: "Failed to download file",
        description: err.message,
      });
    }
  };

  const handleClosePreview = () => {
    setPreviewVisible(false);
    setSelectedDoc(null);
  };

  const handleCloseUpload = () => {
    setUploadVisible(false);
    setDocumentType(null);
  };

  const handleUploadSuccess = () => {
    refetch();
  };

  return (
    <div className="space-y-3 md:space-y-4">
      <Title level={3} className="mb-0 text-lg">
        Documents
      </Title>

      <DocumentTypeSelection onSelectType={handleCreateDocument} />

      <DocumentList
        documents={documents}
        isLoading={isLoading}
        onView={viewDocument}
        onDownload={downloadDocument}
      />

      <Modal
        title={selectedDoc?.title || "View Document"}
        open={previewVisible}
        onCancel={handleClosePreview}
        footer={[
          <Button
            key="download"
            type="primary"
            onClick={() => selectedDoc && downloadDocument(selectedDoc)}
          >
            Download File
          </Button>,
          <Button key="close" onClick={handleClosePreview}>
            Close
          </Button>,
        ]}
        width={window.innerWidth < 768 ? "95%" : window.innerWidth < 1280 ? "80%" : 800}
      >
        {selectedDoc && <DocumentPreview doc={selectedDoc} />}
      </Modal>

      <DocumentUploadModal
        visible={uploadVisible}
        documentType={documentType}
        onClose={handleCloseUpload}
        onSuccess={handleUploadSuccess}
      />
    </div>
  );
};
